import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { allEquipmentTypesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type EquipmentType, type EquipmentTypeId, isOfferableType, type SpaceId } from '@rock/types';
import { ChevronsUpDown, Plus, X } from 'lucide-react';
import { type FC, type ReactNode, useMemo, useState } from 'react';

interface EquipmentTypeSelectProps {
    /** Types already chosen — left out of the list. */
    selected: EquipmentTypeId[];
    /** One type, or every type of a group when `allowGroups` and the admin clicks the group row. */
    onPick: (typeIds: EquipmentTypeId[]) => void;
    /** The options on offer; the whole catalog when absent. Retired types are always left out. */
    types?: EquipmentType[];
    /** Offer each catalog group as a row that picks all of its (unselected) types at click time. */
    allowGroups?: boolean;
    /** Lists this space's groups first. */
    preferSpaceId?: SpaceId;
    trigger?: ReactNode;
    disabled?: boolean;
}

/**
 * The catalog type picker: a search box over title and id, options grouped the way the catalog groups
 * them, each with its blue id badge. Replaces the plain `<Select>` wherever a type is chosen — the catalog
 * is too long to scroll. Groups are a convenience for picking, never a stored value: a group row expands
 * to its ids at click time.
 */
export const EquipmentTypeSelect: FC<EquipmentTypeSelectProps> = ({
    selected,
    onPick,
    types,
    allowGroups,
    preferSpaceId,
    trigger,
    disabled,
}) => {
    const catalog = useAsyncAtomValue(allEquipmentTypesState, []);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const groups = useMemo(() => {
        const offered = (types ?? catalog).filter(isOfferableType);
        return groupOptions(offered, selected, query, preferSpaceId);
    }, [types, catalog, selected, query, preferSpaceId]);

    const pick = (typeIds: EquipmentTypeId[]) => {
        onPick(typeIds);
        setQuery('');
        setOpen(false);
    };

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setQuery('');
            }}
        >
            <PopoverTrigger asChild disabled={disabled}>
                {trigger ?? (
                    <Button variant="outline" size="sm" type="button" disabled={disabled}>
                        <Plus className="h-3.5 w-3.5" /> Type
                    </Button>
                )}
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-2" align="start">
                <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                        const first = groups[0]?.types[0];
                        if (event.key === 'Enter' && first) {
                            event.preventDefault();
                            pick([first.id]);
                        }
                    }}
                    placeholder="Search types by title or id…"
                    autoComplete="off"
                    autoFocus
                    className="h-8"
                />
                <div className="mt-2 max-h-[320px] overflow-auto">
                    {groups.map((group) => (
                        <div key={group.name} className="mb-1">
                            {allowGroups ? (
                                <button
                                    type="button"
                                    className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground hover:bg-accent hover:text-foreground"
                                    title={`Add every ${group.name} type`}
                                    onClick={() => pick(group.types.map((type) => type.id))}
                                >
                                    {group.name}
                                    <span className="font-normal normal-case tracking-normal">
                                        all {group.types.length}
                                    </span>
                                </button>
                            ) : (
                                <div className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    {group.name}
                                </div>
                            )}
                            {group.types.map((type) => (
                                <button
                                    key={type.id}
                                    type="button"
                                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-accent"
                                    onClick={() => pick([type.id])}
                                >
                                    <span className="truncate">{type.title}</span>
                                    <EquipmentTypeBadge
                                        typeId={type.id}
                                        title={type.title}
                                        className="ml-auto shrink-0"
                                    />
                                </button>
                            ))}
                        </div>
                    ))}
                    {groups.length === 0 && (
                        <div className="px-2 py-1 text-sm text-muted-foreground">
                            {query ? 'No types match.' : 'Every type is already selected.'}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

/**
 * A single-value form control over the same picker: the chosen type as its badge and title, a clear
 * button, and the search popover on click. For the spots that store one `typeId`.
 */
export const EquipmentTypeField: FC<{
    value: EquipmentTypeId | undefined;
    onChange: (typeId: EquipmentTypeId | undefined) => void;
    types?: EquipmentType[];
    preferSpaceId?: SpaceId;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}> = ({ value, onChange, types, preferSpaceId, placeholder = 'Select a type', disabled, className }) => {
    const catalog = useAsyncAtomValue(allEquipmentTypesState, []);
    // The chosen type resolves from the whole catalog: a retired one still shows, muted.
    const chosen = catalog.find((type) => type.id === value) ?? types?.find((type) => type.id === value);
    return (
        <div className={`flex items-center gap-1 ${className ?? ''}`}>
            <EquipmentTypeSelect
                selected={value ? [value] : []}
                onPick={(ids) => onChange(ids[0])}
                types={types}
                preferSpaceId={preferSpaceId}
                disabled={disabled}
                trigger={
                    <Button
                        variant="outline"
                        type="button"
                        disabled={disabled}
                        className="h-9 min-w-0 flex-1 justify-between gap-2 px-3 font-normal"
                    >
                        {value ? (
                            <span className="flex min-w-0 items-center gap-2">
                                <EquipmentTypeBadge typeId={value} />
                                <span className="truncate">{chosen?.title}</span>
                            </span>
                        ) : (
                            <span className="text-muted-foreground">{placeholder}</span>
                        )}
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                }
            />
            {value && !disabled && (
                <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className="h-9 w-9 shrink-0"
                    onClick={() => onChange(undefined)}
                    aria-label="Clear type"
                >
                    <X className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
};

interface TypeGroup {
    name: string;
    types: EquipmentType[];
}

function groupOptions(
    types: EquipmentType[],
    selected: EquipmentTypeId[],
    query: string,
    preferSpaceId: SpaceId | undefined,
): TypeGroup[] {
    const needle = query.trim().toLowerCase();
    const byGroup = new Map<string, EquipmentType[]>();
    for (const type of types) {
        if (selected.includes(type.id)) continue;
        if (needle && !type.id.includes(needle) && !type.title.toLowerCase().includes(needle)) continue;
        const list = byGroup.get(type.group) ?? [];
        list.push(type);
        byGroup.set(type.group, list);
    }
    return [...byGroup.entries()]
        .map(([name, list]) => ({ name, types: [...list].sort((a, b) => a.order - b.order) }))
        .sort((a, b) => {
            const home =
                Number(b.types.some((t) => t.spaceId === preferSpaceId)) -
                Number(a.types.some((t) => t.spaceId === preferSpaceId));
            return home || a.name.localeCompare(b.name);
        });
}
